import { Controller } from "stimulus"

const RECAPTCHA_HOSTS = ["www.recaptcha.net", "www.google.com"]

export default class extends Controller {
  static targets = ["form", "submit"]

  static values = {
    siteKey: String,
    action: String,
    cspNonce: String,
    skip: Boolean
  }

  connect() {
    this.tokenRefreshedForSubmit = false
    this.handleSubmit = this.handleSubmit.bind(this)

    // Skip in test, and when the v2 checkbox fallback omitted the v3 input
    // (loading v3 would race with recaptcha_tags' API script).
    if (this.skipValue || !this.v3ResponseInput) return

    this.readyPromise = this.loadRecaptcha()

    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", this.handleSubmit)
    }
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("submit", this.handleSubmit)
    }
  }

  get v3ResponseInput() {
    if (!this.hasActionValue) return null

    return document.getElementById(`g-recaptcha-response-data-${this.actionValue}`)
  }

  loadRecaptcha() {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => resolve())
        return
      }

      const existing = document.querySelector("script[src*='recaptcha/api.js']")
      if (existing) {
        existing.addEventListener("load", () => {
          if (window.grecaptcha?.ready) {
            window.grecaptcha.ready(() => resolve())
          } else {
            reject(new Error("Failed to load reCAPTCHA"))
          }
        })
        existing.addEventListener("error", () => this.injectScript(0, resolve, reject))
        return
      }

      this.injectScript(0, resolve, reject)
    })
  }

  injectScript(hostIndex, resolve, reject) {
    const host = RECAPTCHA_HOSTS[hostIndex]
    if (!host) {
      reject(new Error("Failed to load reCAPTCHA"))
      return
    }

    const script = document.createElement("script")
    // recaptcha.net first: Google's host when Incognito blocks google.com cookies.
    script.src = `https://${host}/recaptcha/api.js?render=${this.siteKeyValue}`

    if (this.hasCspNonceValue) script.nonce = this.cspNonceValue

    script.onload = () => {
      window.grecaptcha.ready(() => resolve())
    }
    script.onerror = () => this.injectScript(hostIndex + 1, resolve, reject)

    document.head.appendChild(script)
  }

  // reCAPTCHA can stall indefinitely (cold start, iOS api2/pat attestation, a
  // ready() callback that never fires). Without a cap, handleSubmit awaits forever
  // with the button disabled and never reaches requestSubmit, so nothing is sent.
  // Bound both the ready-wait and execute; on timeout the promise rejects, the
  // caller's catch clears the token, and submit proceeds to the v2 checkbox fallback.
  withTimeout(promise, ms, label) {
    return Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error(`reCAPTCHA ${label} timeout`)), ms))
    ])
  }

  async refreshToken() {
    await this.withTimeout(this.readyPromise, 10000, "ready")
    const token = await this.withTimeout(
      window.grecaptcha.execute(this.siteKeyValue, { action: this.actionValue }),
      10000,
      "execute"
    )
    const inputElement = this.v3ResponseInput
    if (inputElement) {
      inputElement.value = token
    }
    return token
  }

  async handleSubmit(event) {
    if (!this.v3ResponseInput) return

    if (this.tokenRefreshedForSubmit) {
      this.tokenRefreshedForSubmit = false
      return
    }

    // form-validations runs first; bail if it already blocked an invalid submit
    if (event.defaultPrevented) return

    event.preventDefault()

    if (this.hasSubmitTarget) this.submitTarget.disabled = true

    try {
      await this.refreshToken()
    } catch (error) {
      // v3 couldn't produce a fresh token. Clear any prior token so we never
      // resend a stale/expired one (which would re-trigger timeout-or-duplicate),
      // then let the submit proceed: the backend fails v3 verification on an empty
      // token and renders the v2 checkbox fallback, so the submission doesn't hard-fail.
      console.error("[reCAPTCHA] Failed to refresh token on submit; falling back to v2 checkbox", error)
      if (this.v3ResponseInput) this.v3ResponseInput.value = ""
    } finally {
      if (this.hasSubmitTarget) this.submitTarget.disabled = false
    }

    this.tokenRefreshedForSubmit = true

    this.formTarget.requestSubmit(this.hasSubmitTarget ? this.submitTarget : undefined)
  }
}
